import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function FashionFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [style, setStyle] = useState("all");
  const [occasion, setOccasion] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedLook, setSelectedLook] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [savedLooks, setSavedLooks] = useState(new Set());
  const [activeTab, setActiveTab] = useState("feed"); // feed, discover, trending

  const handleEditPost = (post) => {
    setSelectedLook(null);
    setEditingPost(post);
  };
  
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedLook(null);
  };

  const categories = [
    { id: "all", name: "Todos" },
    { id: "outfit", name: "Look Completo" },
    { id: "accessory", name: "Acessórios" },
    { id: "shoes", name: "Calçados" },
    { id: "bag", name: "Bolsas" },
    { id: "jewelry", name: "Jóias" },
    { id: "trend", name: "Tendências" },
  ];

  const styles = [
    { id: "all", name: "Todos" },
    { id: "Casual", name: "Casual" },
    { id: "Formal", name: "Formal" },
    { id: "Streetwear", name: "Streetwear" },
    { id: "Vintage", name: "Vintage" },
    { id: "Minimalista", name: "Minimalista" },
    { id: "Boho", name: "Boho" },
  ];

  const occasions = [
    { id: "all", name: "Todas" },
    { id: "Casual", name: "Casual" },
    { id: "Trabalho", name: "Trabalho" },
    { id: "Festa", name: "Festa" },
    { id: "Casamento", name: "Casamento" },
    { id: "Praia", name: "Praia" },
    { id: "Esporte", name: "Esporte" },
  ];

  const fetchPosts = useCallback(async (pageNum = 1, reset = false) => {
    try {
      setLoading(true);
      let url;
      
      if (activeTab === "feed") {
        url = `${process.env.REACT_APP_BACKEND_URL}/feed/fashion?page=${pageNum}&limit=12`;
      } else if (activeTab === "discover") {
        url = `${process.env.REACT_APP_BACKEND_URL}/feed/fashion/public?page=${pageNum}&limit=12&sort=trending`;
      } else if (activeTab === "trending") {
        url = `${process.env.REACT_APP_BACKEND_URL}/feed/fashion/trending?limit=20&period=week`;
      }

      if (category !== "all") url += `&category=${category}`;
      if (style !== "all") url += `&style=${style}`;
      if (occasion !== "all") url += `&occasion=${occasion}`;

      const { data } = await axios.get(url, { 
        headers: { Authorization: `Bearer ${user.token}` } 
      });

      const newPosts = data.posts || data || [];
      
      if (reset || pageNum === 1) {
        setPosts(newPosts);
      } else {
        setPosts((prev) => [...prev, ...newPosts]);
      }
      
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fashion:", error);
      setLoading(false);
    }
  }, [activeTab, category, style, occasion, user.token]);

  const fetchSavedLooks = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/fashion/saved`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const saved = new Set(data.savedLooks?.map(s => s.post._id || s.post) || []);
      setSavedLooks(saved);
    } catch (error) {
      console.error("Error fetching saved looks:", error);
    }
  }, [user.token]);

  const handleSaveLook = async (postId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/fashion/save`,
        { postId },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSavedLooks(prev => new Set([...prev, postId]));
    } catch (error) {
      console.error("Error saving look:", error);
      alert(error.response?.data?.message || "Erro ao salvar look");
    }
  };

  const handleUnsaveLook = async (postId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/fashion/save/${postId}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSavedLooks(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    } catch (error) {
      console.error("Error unsaving look:", error);
    }
  };

  const handleShareLook = async (post) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.fashionData?.title || "Look Fashion",
          text: post.fashionData?.description || "",
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copiado!");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  useEffect(() => { 
    setPage(1); 
    fetchPosts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [category, style, occasion, activeTab]);

  useEffect(() => {
    fetchSavedLooks();
  }, [fetchSavedLooks]);

  if (loading && posts.length === 0) {
    return <div className="fashion_feed_loading"><div className="fashion_loader"></div><span>Carregando looks...</span></div>;
  }

  return (
    <div className="fashion_feed">
      {/* Tabs */}
      <div className="fashion_tabs">
        <button 
          className={`fashion_tab ${activeTab === "feed" ? "active" : ""}`}
          onClick={() => setActiveTab("feed")}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
          </svg>
          Meu Feed
        </button>
        <button 
          className={`fashion_tab ${activeTab === "discover" ? "active" : ""}`}
          onClick={() => setActiveTab("discover")}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 10.9c-.61 0-1.1.49-1.1 1.1s.49 1.1 1.1 1.1c.61 0 1.1-.49 1.1-1.1s-.49-1.1-1.1-1.1zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm2.19 12.19L6 18l3.81-8.19L18 6l-3.81 8.19z"/>
          </svg>
          Descobrir
        </button>
        <button 
          className={`fashion_tab ${activeTab === "trending" ? "active" : ""}`}
          onClick={() => setActiveTab("trending")}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
          </svg>
          Trending
        </button>
      </div>

      {/* Filtros */}
      <div className="fashion_filters_container">
        <div className="fashion_filter_group">
          <label>Categoria</label>
          <div className="simple_filter_bar">
            <div className="filter_buttons">
              {categories.map((filter) => (
                <button
                  key={filter.id}
                  className={`filter_btn ${category === filter.id ? "active" : ""}`}
                  onClick={() => setCategory(filter.id)}
                >
                  {filter.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="fashion_filter_group">
          <label>Estilo</label>
          <div className="simple_filter_bar">
            <div className="filter_buttons">
              {styles.map((filter) => (
                <button
                  key={filter.id}
                  className={`filter_btn ${style === filter.id ? "active" : ""}`}
                  onClick={() => setStyle(filter.id)}
                >
                  {filter.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="fashion_filter_group">
          <label>Ocasião</label>
          <div className="simple_filter_bar">
            <div className="filter_buttons">
              {occasions.map((filter) => (
                <button
                  key={filter.id}
                  className={`filter_btn ${occasion === filter.id ? "active" : ""}`}
                  onClick={() => setOccasion(filter.id)}
                >
                  {filter.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="fashion_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M21.6 18.2L13 11.75v-.91c1.65-.49 2.8-2.17 2.43-4.05-.26-1.31-1.3-2.4-2.61-2.7C10.54 3.57 8.5 5.3 8.5 7.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5c0 .84-.69 1.52-1.53 1.5-.54-.01-.97.45-.97.99v1.76L2.4 18.2c-.77.58-.36 1.8.6 1.8h18c.96 0 1.37-1.22.6-1.8zM6 18l6-4.5 6 4.5H6z"/>
          </svg>
          <h3>Nenhum look encontrado</h3>
          <p>Compartilhe seu estilo!</p>
        </div>
      ) : (
        <div className="fashion_grid">
          {posts.map((post) => (
            <div key={post._id} className="fashion_card" onClick={() => setSelectedLook(post)}>
              <div className="fashion_card_image">
                <img src={post.fashionData?.photos?.[0] || post.images?.[0]?.url || post.images?.[0] || "/images/default-fashion.png"} alt="" />
                <div className="fashion_overlay">
                  <span className="fashion_occasion">
                    {categories.find(c => c.id === post.fashionData?.occasion)?.name || "Look"}
                  </span>
                </div>
              </div>
              <div className="fashion_card_content">
                <h3>{post.fashionData?.title || post.text}</h3>
                {post.fashionData?.lookItems?.length > 0 && (
                  <div className="fashion_items_preview">
                    {post.fashionData.lookItems.slice(0, 3).map((item, i) => (
                      <span key={i}>{item.name}</span>
                    ))}
                    {post.fashionData.lookItems.length > 3 && (
                      <span>+{post.fashionData.lookItems.length - 3}</span>
                    )}
                  </div>
                )}
              <div className="fashion_footer">
                  <Link to={`/profile/${post.user.username}`} className="fashion_stylist" onClick={(e) => e.stopPropagation()}>
                    <img src={post.user.picture} alt="" />
                    <span>{post.user.first_name}</span>
                  </Link>
                  {post.fashionData?.items && post.fashionData.items.length > 0 && (
                    <span className="fashion_items_count">{post.fashionData.items.length} peças</span>
                  )}
                  <div className="fashion_card_actions" onClick={(e) => e.stopPropagation()}>
                    <button 
                      className={`fashion_save_icon ${savedLooks.has(post._id) ? "saved" : ""}`}
                      onClick={() => savedLooks.has(post._id) ? handleUnsaveLook(post._id) : handleSaveLook(post._id)}
                      title={savedLooks.has(post._id) ? "Remover dos salvos" : "Salvar look"}
                    >
                      <svg viewBox="0 0 24 24" fill={savedLooks.has(post._id) ? "currentColor" : "none"} stroke="currentColor" width="20" height="20">
                        <path strokeWidth="2" d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                      </svg>
                    </button>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="fashion_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais looks"}
          </button>
        </div>
      )}

      {selectedLook && (
        <div className="fashion_modal_overlay" onClick={() => setSelectedLook(null)}>
          <div className="fashion_modal" onClick={(e) => e.stopPropagation()}>
            <button className="fashion_modal_close" onClick={() => setSelectedLook(null)}>✕</button>

            <div className="fashion_modal_gallery">
              <img src={selectedLook.fashionData?.photos?.[0] || selectedLook.images?.[0]} alt="" />
              {selectedLook.fashionData?.photos?.length > 1 && (
                <div className="fashion_gallery_thumbs">
                  {selectedLook.fashionData.photos.slice(1, 5).map((photo, i) => (
                    <img key={i} src={photo} alt="" />
                  ))}
                </div>
              )}
            </div>

            <div className="fashion_modal_body">
              <span className="fashion_modal_occasion">
                {categories.find(c => c.id === selectedLook.fashionData?.occasion)?.name}
              </span>
              <h2>{selectedLook.fashionData?.title}</h2>

              {selectedLook.fashionData?.style && (
                <div className="fashion_modal_style">
                  <span>Estilo: <strong>{selectedLook.fashionData.style}</strong></span>
                </div>
              )}

              {selectedLook.fashionData?.description && (
                <p className="fashion_modal_desc">{selectedLook.fashionData.description}</p>
              )}

              {selectedLook.fashionData?.lookItems?.length > 0 && (
                <div className="fashion_modal_items">
                  <h4>Itens do look</h4>
                  <div className="fashion_items_list">
                    {selectedLook.fashionData.lookItems.map((item, i) => (
                      <div key={i} className="fashion_item">
                        {item.image && <img src={item.image} alt="" />}
                        <div className="fashion_item_info">
                          <span className="fashion_item_category">{item.category}</span>
                          <span className="fashion_item_name">{item.name}</span>
                          {item.brand && <span className="fashion_item_brand">{item.brand}</span>}
                          {item.price > 0 && (
                            <span className="fashion_item_price">R$ {item.price.toLocaleString("pt-BR")}</span>
                          )}
                        </div>
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noreferrer" className="fashion_item_link">
                            Comprar
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLook.fashionData?.colors?.length > 0 && (
                <div className="fashion_modal_colors">
                  <h4>Paleta de cores</h4>
                  <div className="fashion_colors_list">
                    {selectedLook.fashionData.colors.map((color, i) => (
                      <span key={i} className="fashion_color" style={{ background: color }}></span>
                    ))}
                  </div>
                </div>
              )}

              <Link to={`/profile/${selectedLook.user.username}`} className="fashion_modal_stylist">
                <img src={selectedLook.user.picture} alt="" />
                <div>
                  <span className="fashion_stylist_label">Estilista</span>
                  <span className="fashion_stylist_name">{selectedLook.user.first_name} {selectedLook.user.last_name}</span>
                </div>
              </Link>

              <div className="fashion_modal_actions">
                <button 
                  className={`fashion_save_btn ${savedLooks.has(selectedLook._id) ? "saved" : ""}`}
                  onClick={() => savedLooks.has(selectedLook._id) ? handleUnsaveLook(selectedLook._id) : handleSaveLook(selectedLook._id)}
                >
                  <svg viewBox="0 0 24 24" fill={savedLooks.has(selectedLook._id) ? "currentColor" : "none"} stroke="currentColor" width="20" height="20">
                    <path strokeWidth="2" d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                  </svg>
                  {savedLooks.has(selectedLook._id) ? "Salvo" : "Salvar look"}
                </button>
                <button className="fashion_share_btn" onClick={() => handleShareLook(selectedLook)}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  Compartilhar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
